import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Search, FileText, ExternalLink, Upload, Eye, Trash2, History } from "lucide-react";
import { toast } from "sonner";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("es-ES", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function GeneratedDocs() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [templates, setTemplates] = useState({});
  const [users, setUsers] = useState({});
  const [q, setQ] = useState("");
  const [filterLexDuo, setFilterLexDuo] = useState("all"); // all | uploaded | pending | none
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data: docs }, { data: tpls }] = await Promise.all([
        api.get("/generator"),
        api.get("/templates"),
      ]);
      setItems(docs || []);
      const tMap = {};
      (tpls || []).forEach((t) => { tMap[t.id] = t; });
      setTemplates(tMap);
      // Users (solo admin puede, ignoramos error)
      try {
        const { data: us } = await api.get("/users");
        const uMap = {};
        (us || []).forEach((u) => { uMap[u.id] = u; });
        setUsers(uMap);
      } catch { /* no admin */ }
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return items.filter((it) => {
      if (needle) {
        const plantilla = (templates[it.template_id]?.name || "").toLowerCase();
        const hay = [
          it.title || "",
          plantilla,
          String(it.expediente_id_lexduo || ""),
          (it.uploaded_to_lexduo?.filename || ""),
        ].join(" ").toLowerCase();
        if (!hay.includes(needle)) return false;
      }
      if (filterLexDuo === "uploaded" && !it.uploaded_to_lexduo) return false;
      if (filterLexDuo === "pending" && (!it.expediente_id_lexduo || it.uploaded_to_lexduo)) return false;
      if (filterLexDuo === "none" && it.expediente_id_lexduo) return false;
      return true;
    });
  }, [items, q, filterLexDuo, templates]);

  const remove = async (id) => {
    if (!window.confirm("¿Eliminar este documento del registro?\nLa actuación en LexDuo no se tocará.")) return;
    try {
      await api.delete(`/generator/${id}`);
      toast.success("Registro eliminado");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const download = async (id, title, fmt) => {
    try {
      const r = await api.get(`/export/${fmt}/${id}`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(title || "documento").replace(/[^a-z0-9_-]+/gi, "_")}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <PageContainer>
      <PageHeader
        title="Documentos generados"
        subtitle="Registro de documentos creados con IA y su trazabilidad hacia expedientes de LexDuo."
      />

      <div className="bg-white border border-[#E5E7EB] rounded-sm p-3 mb-4 flex flex-col md:flex-row md:items-center gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar por título, plantilla, nº expediente LexDuo…"
            className="rounded-sm pl-9 h-9"
            data-testid="generated-search"
          />
        </div>
        <div className="flex items-center gap-2 text-xs">
          <span className="uppercase tracking-wider text-neutral-500">Filtro LexDuo:</span>
          {[
            { v: "all", l: "Todos" },
            { v: "uploaded", l: "Subidos" },
            { v: "pending", l: "Pendientes" },
            { v: "none", l: "Sin expediente" },
          ].map((opt) => (
            <button
              key={opt.v}
              onClick={() => setFilterLexDuo(opt.v)}
              data-testid={`filter-${opt.v}`}
              className={
                "px-2.5 py-1 rounded-sm border text-[11px] uppercase tracking-wider transition-colors " +
                (filterLexDuo === opt.v
                  ? "bg-[#7B1826] text-white border-[#7B1826]"
                  : "bg-white text-neutral-600 border-[#E5E7EB] hover:border-[#7B1826]/40")
              }
            >
              {opt.l}
            </button>
          ))}
        </div>
        <Button
          onClick={() => nav("/generador")}
          data-testid="go-generator-button"
          className="rounded-sm bg-[#7B1826] hover:bg-[#5D121D]"
        >
          Nuevo documento
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-20 text-neutral-500 text-sm">Cargando…</div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-20 border border-dashed border-[#E5E7EB] bg-white">
          <History className="w-10 h-10 mx-auto text-neutral-400" />
          <p className="mt-4 text-sm text-neutral-500">
            {items.length === 0
              ? "Aún no has generado documentos. Empieza desde el Generador IA."
              : "Sin resultados con los filtros actuales."}
          </p>
        </div>
      ) : (
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-sm min-w-[960px]">
            <thead>
              <tr className="bg-[#F3F4F6] text-[11px] uppercase tracking-wider text-neutral-600">
                <th className="text-left py-3 px-4">Documento</th>
                <th className="text-left py-3 px-4">Plantilla</th>
                <th className="text-left py-3 px-4">Expediente LexDuo</th>
                <th className="text-left py-3 px-4">Destino</th>
                <th className="text-left py-3 px-4">Autor</th>
                <th className="text-left py-3 px-4">Fecha</th>
                <th className="text-right py-3 px-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((it) => {
                const tpl = templates[it.template_id];
                const autor = users[it.created_by];
                const up = it.uploaded_to_lexduo;
                return (
                  <tr key={it.id} className="border-t border-[#E5E7EB] hover:bg-[#FBFBF9]" data-testid={`gen-row-${it.id}`}>
                    <td className="py-3 px-4">
                      <div className="font-medium text-[#0F172A] truncate max-w-[260px]">
                        <FileText className="w-3.5 h-3.5 inline-block mr-1.5 text-neutral-400" />
                        {it.title}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-neutral-600 truncate max-w-[200px]">
                      {tpl?.name || <span className="text-neutral-400 italic">(sin plantilla)</span>}
                    </td>
                    <td className="py-3 px-4">
                      {it.expediente_id_lexduo ? (
                        <Badge variant="outline" className="rounded-sm text-[10px] border-[#7B1826]/30 text-[#7B1826]">
                          LexDuo #{it.expediente_id_lexduo}
                        </Badge>
                      ) : (
                        <span className="text-neutral-400 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {up ? (
                        <div className="flex flex-col gap-0.5">
                          <Badge className="rounded-sm text-[10px] bg-emerald-100 text-emerald-800 hover:bg-emerald-100 w-fit">
                            <Upload className="w-3 h-3 mr-1" />
                            Adjuntado a LexDuo
                          </Badge>
                          <span className="text-[11px] text-neutral-500 font-mono truncate max-w-[200px]" title={up.filename}>
                            {up.filename} · {fmtDate(up.uploaded_at)}
                          </span>
                        </div>
                      ) : it.expediente_id_lexduo ? (
                        <Badge variant="outline" className="rounded-sm text-[10px] border-amber-300 text-amber-700">
                          Pendiente de subir
                        </Badge>
                      ) : (
                        <span className="text-neutral-400 text-xs">Local</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-neutral-600 text-xs">
                      {autor?.name || <span className="text-neutral-400 font-mono">{it.created_by?.slice(0, 8) || "—"}</span>}
                    </td>
                    <td className="py-3 px-4 text-neutral-600 text-xs whitespace-nowrap">{fmtDate(it.created_at)}</td>
                    <td className="py-3 px-4 text-right">
                      <div className="inline-flex items-center gap-0.5">
                        <button
                          onClick={() => nav(`/generador?gen=${it.id}`)}
                          className="p-1.5 text-neutral-500 hover:text-[#7B1826]"
                          title="Ver / editar"
                          data-testid={`gen-view-${it.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => download(it.id, it.title, "docx")}
                          className="p-1.5 text-neutral-500 hover:text-[#7B1826]"
                          title="Descargar DOCX"
                          data-testid={`gen-download-${it.id}`}
                        >
                          <ExternalLink className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(it.id)}
                          className="p-1.5 text-neutral-500 hover:text-[#DC2626]"
                          title="Eliminar del registro"
                          data-testid={`gen-delete-${it.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </PageContainer>
  );
}
